<template>
  <div>
    <!-- Header -->
    <div class="page-header">
      <div>
        <button class="back-btn" @click="$router.push('/procurement/sourcing-events')">
          <font-awesome-icon icon="arrow-left" class="me-1" /> Sourcing Events
        </button>
        <div class="title-row">
          <h5 class="page-title" v-if="event">{{ event.event_number }}: {{ event.title }}</h5>
          <span v-if="event" :class="['status-badge', `status-badge--${event.status}`]">{{ formatStatus(event.status) }}</span>
          <span v-if="event" :class="['type-badge', `type-badge--${event.event_type}`]">{{ event.event_type.toUpperCase() }}</span>
        </div>
        <p class="page-sub" v-if="event && event.pr">
          Linked PR: <span class="mono">{{ event.pr.pr_number }}</span> — {{ event.pr.title }}
        </p>
      </div>
      <div class="header-actions" v-if="event">
        <button v-if="event.status === 'draft'" class="btn-outline" @click="$router.push(`/procurement/sourcing-events/${event.id}/edit`)">
          <font-awesome-icon icon="edit" class="me-1" /> Edit
        </button>
        <button v-if="event.status === 'draft'" class="btn-primary" :disabled="actioning" @click="issueEvent">
          <font-awesome-icon icon="paper-plane" class="me-1" /> Issue to Vendors
        </button>
        <button v-if="event.status === 'open'" class="btn-warning" :disabled="actioning" @click="showCloseModal = true">
          <font-awesome-icon icon="lock" class="me-1" /> Close Event
        </button>
        <button v-if="event.status === 'closed'" class="btn-outline" :disabled="actioning" @click="setStatus('evaluation')">
          Move to Evaluation
        </button>
        <button
          v-if="event.bids_visible && submittedVendorCount > 0"
          class="btn-outline btn-analysis"
          @click="$router.push(`/procurement/sourcing-events/${event.id}/analysis`)"
        >
          <font-awesome-icon icon="chart-bar" class="me-1" /> Bid Analysis
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading...</p></div>

    <div v-else-if="event" class="detail-layout">

      <!-- Left column: Line items, vendors, clarifications -->
      <div class="main-col">

        <!-- Tabs -->
        <div class="tab-bar">
          <button :class="['tab-btn', activeTab === 'lines' ? 'active' : '']" @click="activeTab = 'lines'">Line Items ({{ event.lines.length }})</button>
          <button :class="['tab-btn', activeTab === 'vendors' ? 'active' : '']" @click="activeTab = 'vendors'">Vendors ({{ event.event_vendors.length }})</button>
          <button :class="['tab-btn', activeTab === 'clarifications' ? 'active' : '']" @click="activeTab = 'clarifications'; loadClarifications()">Clarifications ({{ clarifications.length }})</button>
        </div>

        <!-- Lines Tab -->
        <div v-show="activeTab === 'lines'" class="tab-pane">
          <div v-if="!event.lines.length" class="empty-state-sm">No line items.</div>
          <table v-else class="inner-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>Category</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>Target Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, i) in event.lines" :key="line.id">
                <td class="text-muted">{{ i + 1 }}</td>
                <td>
                  <div class="line-desc">{{ line.description }}</div>
                  <div class="line-spec text-muted" v-if="line.specification">{{ line.specification }}</div>
                </td>
                <td>{{ line.category ? line.category.name : '—' }}</td>
                <td>{{ line.quantity }}</td>
                <td>{{ line.uom || '—' }}</td>
                <td>
                  <span v-if="line.target_unit_cost">{{ formatCurrency(line.target_unit_cost, event.currency) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Vendors Tab -->
        <div v-show="activeTab === 'vendors'" class="tab-pane">
          <div class="tab-pane-header">
            <span class="tab-pane-title">Invited Vendors</span>
            <button v-if="['draft','open'].includes(event.status)" class="btn-sm-add" @click="showInviteModal = true">
              <font-awesome-icon icon="plus" /> Invite Vendor
            </button>
          </div>

          <div v-if="!event.event_vendors.length" class="empty-state-sm">
            No vendors invited yet.
          </div>

          <div v-else class="vendor-list">
            <div v-for="ev in event.event_vendors" :key="ev.id" class="vendor-card">
              <div class="vendor-card-header">
                <div>
                  <div class="vendor-name">{{ ev.vendor.name }}</div>
                  <div class="vendor-num text-muted">{{ ev.vendor.vendor_number }}</div>
                </div>
                <div class="vendor-card-right">
                  <span :class="['vendor-status', `vendor-status--${ev.status}`]">{{ formatVendorStatus(ev.status) }}</span>
                  <span v-if="ev.is_recommended" class="recommended-badge">Recommended</span>
                </div>
              </div>

              <!-- Bid locked notice -->
              <div v-if="ev.status === 'submitted' && !event.bids_visible" class="bid-locked-notice">
                <font-awesome-icon icon="lock" class="me-1" />
                Bid submitted — amounts hidden until deadline passes
              </div>

              <div v-if="ev.quoted_total && event.bids_visible" class="quote-row">
                <span class="quote-label">Quote:</span>
                <span class="quote-amount">{{ formatCurrency(ev.quoted_total, ev.quoted_currency || event.currency) }}</span>
                <span class="text-muted" v-if="ev.quote_submitted_at">· {{ formatDate(ev.quote_submitted_at) }}</span>
                <button
                  v-if="ev.status === 'submitted' && ev.quote_lines && ev.quote_lines.length"
                  class="btn-sm btn-view-bid"
                  @click="ev._bidOpen = !ev._bidOpen"
                >
                  {{ ev._bidOpen ? 'Hide Bid' : 'View Bid' }}
                </button>
              </div>

              <!-- Bid breakdown panel -->
              <div v-if="ev._bidOpen && ev.quote_lines && ev.quote_lines.length" class="bid-breakdown">
                <div class="bid-breakdown-meta">
                  <span class="bid-meta-chip">
                    <span class="bid-meta-label">Currency</span>
                    <span class="bid-meta-val">{{ ev.quoted_currency }}</span>
                  </span>
                  <span class="bid-meta-chip">
                    <span class="bid-meta-label">Total</span>
                    <span class="bid-meta-val bid-meta-total">{{ formatCurrency(ev.quoted_total, ev.quoted_currency) }}</span>
                  </span>
                  <span v-if="ev.quote_submitted_at" class="bid-meta-chip">
                    <span class="bid-meta-label">Submitted</span>
                    <span class="bid-meta-val">{{ formatDate(ev.quote_submitted_at) }}</span>
                  </span>
                </div>

                <div class="table-wrap">
                  <table class="bid-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>Qty Requested</th>
                        <th>Quoted Qty</th>
                        <th class="ta-r">Unit Price</th>
                        <th class="ta-r">Line Total</th>
                        <th>Lead Time</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(ql, qi) in ev.quote_lines" :key="ql.id">
                        <td class="text-muted">{{ qi + 1 }}</td>
                        <td>
                          {{ getEventLine(ql.event_line_id)?.description || '—' }}
                          <span v-if="getEventLine(ql.event_line_id)?.specification" class="spec-note">
                            {{ getEventLine(ql.event_line_id).specification }}
                          </span>
                        </td>
                        <td class="text-muted">
                          {{ getEventLine(ql.event_line_id)?.quantity || '—' }}
                          {{ getEventLine(ql.event_line_id)?.uom || '' }}
                        </td>
                        <td class="fw-bold">{{ ql.quantity }}</td>
                        <td class="ta-r">{{ formatCurrency(ql.unit_price, ev.quoted_currency) }}</td>
                        <td class="ta-r fw-bold">{{ formatCurrency(ql.total, ev.quoted_currency) }}</td>
                        <td class="text-muted">{{ ql.lead_time_days ? ql.lead_time_days + ' days' : '—' }}</td>
                        <td class="text-muted" style="max-width:140px;white-space:pre-wrap;">{{ ql.notes || '—' }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="bid-tfoot">
                        <td colspan="5" class="bid-tfoot-label">Grand Total ({{ ev.quoted_currency }})</td>
                        <td class="ta-r bid-tfoot-total">{{ formatCurrency(ev.quoted_total, ev.quoted_currency) }}</td>
                        <td colspan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div v-if="ev.evaluation_notes" class="bid-cover-note">
                  <span class="bid-meta-label" style="display:block;margin-bottom:4px;">Cover Note</span>
                  <p style="margin:0;font-size:13px;color:#374151;white-space:pre-wrap;">{{ ev.evaluation_notes }}</p>
                </div>
              </div>

              <div v-else-if="!ev.quote_lines?.length && ev.evaluation_notes" class="eval-notes">{{ ev.evaluation_notes }}</div>

              <!-- Open event: allow vendor to update submitted bid -->
              <div v-if="event.status === 'open' && ev.status === 'submitted'" class="vendor-requote-row">
                <span v-if="ev.allow_requote" class="requote-enabled-badge">Requote enabled</span>
                <button
                  v-if="!ev.allow_requote"
                  class="btn-sm btn-sm--amber"
                  :disabled="ev._allowingRequote"
                  @click="allowRequote(ev)"
                >
                  {{ ev._allowingRequote ? 'Enabling…' : 'Allow Bid Update' }}
                </button>
              </div>

              <div class="vendor-actions" v-if="['closed','evaluation','award_recommended'].includes(event.status)">
                <div class="quote-fields">
                  <input v-model.number="ev._quote" type="number" min="0" step="any" class="mini-input" placeholder="Quote amount" />
                  <input v-model="ev._eval" class="mini-input" placeholder="Evaluation notes" />
                  <input v-model.number="ev._rank" type="number" min="1" class="mini-input" style="width:60px" placeholder="Rank" />
                  <button class="btn-sm" @click="updateVendorRecord(ev)">Save</button>
                </div>
                <div class="vendor-action-btns">
                  <button v-if="!ev.is_recommended && ev.status !== 'disqualified'" class="btn-sm btn-sm--blue" @click="recommendVendor(ev)">Recommend</button>
                  <button v-if="event.status === 'award_recommended' && ev.is_recommended" class="btn-sm btn-sm--green" @click="awardVendor(ev)">Award Contract</button>
                  <button
                    v-if="ev.status === 'submitted' && ['evaluation','award_recommended'].includes(event.status) && event.bids_visible"
                    class="btn-sm btn-sm--teal"
                    @click="openGeneratePo(ev)"
                  >
                    <font-awesome-icon icon="file-alt" class="me-1" /> Generate PO
                  </button>
                  <button v-if="['open','evaluation'].includes(event.status)" class="btn-sm btn-sm--danger" @click="removeVendor(ev)">Remove</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Clarifications Tab -->
        <div v-show="activeTab === 'clarifications'" class="tab-pane">
          <div class="tab-pane-header">
            <span class="tab-pane-title">Clarifications</span>
            <button v-if="event.status !== 'cancelled'" class="btn-sm-add" @click="showClarModal = true">
              <font-awesome-icon icon="plus" /> Add
            </button>
          </div>

          <div v-if="!clarifications.length" class="empty-state-sm">No clarifications logged.</div>

          <div v-else class="clarification-list">
            <div v-for="c in clarifications" :key="c.id" class="clar-card">
              <div class="clar-header">
                <div class="clar-meta">
                  <span class="clar-asker">{{ c.asked_by }}</span>
                  <span v-if="c.vendor" class="clar-vendor text-muted">({{ c.vendor.name }})</span>
                  <span v-if="c.is_public" class="public-badge">Public</span>
                </div>
                <span class="text-muted" style="font-size:11px;">{{ formatDate(c.created_at) }}</span>
              </div>
              <div class="clar-question">{{ c.question }}</div>
              <div v-if="c.answer" class="clar-answer">
                <font-awesome-icon icon="reply" class="me-1" />
                {{ c.answer }}
                <span class="text-muted" style="font-size:11px; margin-left:8px;">{{ formatDate(c.answered_at) }}</span>
              </div>
              <div v-else class="clar-unanswered">
                <div class="answer-form">
                  <textarea v-model="c._answer" class="field-input" rows="2" placeholder="Type your answer..."></textarea>
                  <div class="answer-form-row">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="c._isPublic" /> Make public (visible to all vendors)
                    </label>
                    <button class="btn-sm btn-sm--blue" @click="answerClarification(c)">Answer</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right sidebar: event meta -->
      <div class="side-col">
        <div class="meta-card">
          <div class="meta-title">Event Details</div>
          <div class="meta-row"><span class="meta-label">Reference</span><span class="mono">{{ event.event_number }}</span></div>
          <div class="meta-row"><span class="meta-label">Type</span><span :class="['type-badge', `type-badge--${event.event_type}`]">{{ event.event_type.toUpperCase() }}</span></div>
          <div class="meta-row" v-if="event.project"><span class="meta-label">Project</span>{{ event.project.project_code }} — {{ event.project.project_name }}</div>
          <div class="meta-row" v-if="event.department"><span class="meta-label">Department</span>{{ event.department.name }}</div>
          <div class="meta-row"><span class="meta-label">Currency</span>{{ event.currency }}</div>
          <div class="meta-row" v-if="event.submission_deadline">
            <span class="meta-label">Deadline</span>
            <span :class="isOverdue(event.submission_deadline) && event.status === 'open' ? 'date-overdue' : ''">
              {{ formatDatetime(event.submission_deadline) }}
            </span>
          </div>
          <div class="meta-row" v-if="event.valid_until"><span class="meta-label">Valid Until</span>{{ formatDate(event.valid_until) }}</div>
          <div class="meta-row" v-if="event.payment_terms"><span class="meta-label">Payment Terms</span>{{ event.payment_terms }}</div>
          <div class="meta-row" v-if="event.delivery_address"><span class="meta-label">Delivery</span>{{ event.delivery_address }}</div>
          <div class="meta-row" v-if="event.issued_at"><span class="meta-label">Issued</span>{{ formatDatetime(event.issued_at) }}</div>
          <div class="meta-row" v-if="event.closed_at"><span class="meta-label">Closed</span>{{ formatDatetime(event.closed_at) }}</div>
          <div class="meta-row" v-if="event.awarded_vendor">
            <span class="meta-label">Awarded To</span>
            <span class="award-winner">{{ event.awarded_vendor.name }}</span>
          </div>
        </div>

        <div class="meta-card" v-if="event.instructions">
          <div class="meta-title">Instructions for Vendors</div>
          <p class="instructions-text">{{ event.instructions }}</p>
        </div>

        <div class="meta-card" v-if="event.notes">
          <div class="meta-title">Internal Notes</div>
          <p class="instructions-text">{{ event.notes }}</p>
        </div>
      </div>
    </div>

    <!-- Invite Vendor Modal -->
    <div v-if="showInviteModal" class="modal-backdrop" @click.self="showInviteModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Invite Vendor</h6>
          <button class="btn-close-modal" @click="showInviteModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field">
            <label class="field-label">Select Vendor <span class="req">*</span></label>
            <select v-model="inviteVendorId" class="field-input">
              <option value="">— Select vendor —</option>
              <option v-for="v in availableVendors" :key="v.id" :value="v.id">{{ v.name }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showInviteModal = false">Cancel</button>
          <button class="btn-primary" :disabled="!inviteVendorId || inviting" @click="doInviteVendor">
            {{ inviting ? 'Inviting...' : 'Invite' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Close Event Confirmation Modal -->
    <div v-if="showCloseModal" class="modal-backdrop" @click.self="showCloseModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header modal-header--warning">
          <div class="modal-header-icon">
            <font-awesome-icon icon="lock" />
          </div>
          <button class="btn-close-modal" @click="showCloseModal = false">&times;</button>
        </div>
        <div class="modal-body modal-body--center">
          <h6 class="modal-confirm-title">Close Sourcing Event?</h6>
          <p class="modal-confirm-desc">
            Closing <strong>{{ event?.event_number }}</strong> will prevent vendors from submitting
            or updating their quotes. This action moves the event to evaluation.
          </p>
          <div class="modal-confirm-meta">
            <div class="modal-confirm-meta-row">
              <span class="modal-confirm-meta-label">Event</span>
              <span class="modal-confirm-meta-val mono">{{ event?.event_number }}</span>
            </div>
            <div class="modal-confirm-meta-row">
              <span class="modal-confirm-meta-label">Vendors invited</span>
              <span class="modal-confirm-meta-val">{{ event?.event_vendors?.length ?? 0 }}</span>
            </div>
            <div class="modal-confirm-meta-row">
              <span class="modal-confirm-meta-label">Quotes received</span>
              <span class="modal-confirm-meta-val">{{ event?.event_vendors?.filter(v => v.status === 'submitted').length ?? 0 }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showCloseModal = false">Cancel</button>
          <button class="btn-warning" :disabled="actioning" @click="closeEvent">
            {{ actioning ? 'Closing...' : 'Yes, Close Event' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Generate PO Modal -->
    <div v-if="showGeneratePoModal" class="modal-backdrop" @click.self="showGeneratePoModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Generate Purchase Order</h6>
          <button class="btn-close-modal" @click="showGeneratePoModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="po-gen-vendor-info" v-if="selectedPoVendor">
            <div class="po-gen-vendor-name">{{ selectedPoVendor.vendor.name }}</div>
            <div class="text-muted">{{ selectedPoVendor.vendor.vendor_number }}</div>
            <div class="po-gen-amount" v-if="selectedPoVendor.quoted_total">
              Quote: <strong>{{ formatCurrency(selectedPoVendor.quoted_total, selectedPoVendor.quoted_currency || event.currency) }}</strong>
            </div>
          </div>
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">PO Title <span class="req">*</span></label>
            <input v-model="generatePoForm.title" class="field-input" placeholder="Purchase order title" />
          </div>
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">Expected Delivery Date</label>
            <input v-model="generatePoForm.expected_delivery_date" type="date" class="field-input" />
          </div>
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">Delivery Address</label>
            <input v-model="generatePoForm.delivery_address" class="field-input" :placeholder="event.delivery_address || 'Delivery address'" />
          </div>
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">Payment Terms</label>
            <select v-model="generatePoForm.payment_terms" class="field-input">
              <option value="">— No payment terms —</option>
              <option v-for="t in paymentTerms" :key="t.id" :value="t.name">{{ t.name }}</option>
            </select>
          </div>
          <div class="field">
            <label class="field-label">Notes</label>
            <textarea v-model="generatePoForm.notes" class="field-input" rows="2" placeholder="Internal notes for this PO"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showGeneratePoModal = false">Cancel</button>
          <button class="btn-primary" :disabled="!generatePoForm.title || generatingPo" @click="doGeneratePo">
            {{ generatingPo ? 'Generating…' : 'Generate PO' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Clarification Modal -->
    <div v-if="showClarModal" class="modal-backdrop" @click.self="showClarModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h6>Log Clarification</h6>
          <button class="btn-close-modal" @click="showClarModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">Vendor (optional)</label>
            <select v-model="clarForm.vendor_id" class="field-input">
              <option value="">— General (not vendor-specific) —</option>
              <option v-for="ev in event.event_vendors" :key="ev.vendor_id" :value="ev.vendor_id">{{ ev.vendor.name }}</option>
            </select>
          </div>
          <div class="field" style="margin-bottom:12px;">
            <label class="field-label">Asked By <span class="req">*</span></label>
            <input v-model="clarForm.asked_by" class="field-input" placeholder="Name of person asking" />
          </div>
          <div class="field">
            <label class="field-label">Question <span class="req">*</span></label>
            <textarea v-model="clarForm.question" class="field-input" rows="3" placeholder="Enter the clarification question..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="showClarModal = false">Cancel</button>
          <button class="btn-primary" :disabled="!clarForm.question || !clarForm.asked_by || addingClar" @click="doAddClarification">
            {{ addingClar ? 'Adding...' : 'Add Clarification' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'SourcingEventDetail',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: true,
      actioning: false,
      event: null,
      activeTab: 'lines',
      clarifications: [],
      clarLoaded: false,
      allVendors: [],
      showCloseModal: false,
      showInviteModal: false,
      inviteVendorId: '',
      inviting: false,
      showClarModal: false,
      clarForm: { vendor_id: '', asked_by: '', question: '' },
      addingClar: false,
      // Generate PO
      showGeneratePoModal: false,
      selectedPoVendor: null,
      generatePoForm: { title: '', delivery_address: '', payment_terms: '', expected_delivery_date: '', notes: '' },
      generatingPo: false,
      paymentTerms: [],
    };
  },
  computed: {
    availableVendors() {
      if (!this.event) return [];
      const invitedIds = new Set(this.event.event_vendors.map(ev => ev.vendor_id));
      return this.allVendors.filter(v => !invitedIds.has(v.id));
    },
    submittedVendorCount() {
      if (!this.event) return 0;
      return this.event.event_vendors.filter(ev => ev.status === 'submitted').length;
    },
  },
  async mounted() {
    const [,,termsRes] = await Promise.allSettled([
      this.loadEvent(),
      this.loadAllVendors(),
      axios.get('/procurement/payment-terms'),
    ]);
    if (termsRes.status === 'fulfilled') {
      this.paymentTerms = termsRes.value?.data?.data ?? [];
    }
  },
  methods: {
    async loadEvent() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/procurement/sourcing-events/${this.$route.params.id}`);
        this.event = data.data;
        // Add reactive temp fields to vendor rows
        (this.event.event_vendors || []).forEach(ev => {
          ev._quote     = ev.quoted_total || '';
          ev._eval      = ev.evaluation_notes || '';
          ev._rank      = ev.rank || '';
          ev._bidOpen   = false;
          ev._allowingRequote = false;
        });
      } catch { this.toast.error('Failed to load event'); }
      finally { this.loading = false; }
    },
    async loadAllVendors() {
      try {
        const { data } = await axios.get('/procurement/vendors', { params: { per_page: 500 } });
        this.allVendors = data.data.data || [];
      } catch (e) { /* non-critical */ }
    },
    async loadClarifications() {
      if (this.clarLoaded) return;
      try {
        const { data } = await axios.get(`/procurement/sourcing-events/${this.event.id}/clarifications`);
        this.clarifications = (data.data || []).map(c => ({ ...c, _answer: '', _isPublic: false }));
        this.clarLoaded = true;
      } catch (e) { /* non-critical */ }
    },
    async issueEvent() {
      this.actioning = true;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/issue`);
        this.toast.success('Event issued — vendor invitations sent');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to issue event');
      } finally { this.actioning = false; }
    },
    async closeEvent() {
      this.actioning = true;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/close`);
        this.showCloseModal = false;
        this.toast.success('Event closed');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to close event');
      } finally { this.actioning = false; }
    },
    async setStatus(status) {
      this.actioning = true;
      try {
        await axios.patch(`/procurement/sourcing-events/${this.event.id}/status`, { status });
        this.toast.success('Status updated');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to update status');
      } finally { this.actioning = false; }
    },
    async doInviteVendor() {
      this.inviting = true;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/vendors`, { vendor_id: this.inviteVendorId });
        this.toast.success('Vendor invited');
        this.showInviteModal = false;
        this.inviteVendorId = '';
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to invite vendor');
      } finally { this.inviting = false; }
    },
    async removeVendor(ev) {
      if (!confirm(`Remove ${ev.vendor.name} from this event?`)) return;
      try {
        await axios.delete(`/procurement/sourcing-events/${this.event.id}/vendors/${ev.vendor_id}`);
        this.toast.success('Vendor removed');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to remove vendor');
      }
    },
    async updateVendorRecord(ev) {
      try {
        await axios.patch(`/procurement/sourcing-events/${this.event.id}/vendors/${ev.vendor_id}`, {
          quoted_total:     ev._quote || null,
          evaluation_notes: ev._eval || null,
          rank:             ev._rank || null,
        });
        this.toast.success('Vendor record updated');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to update vendor record');
      }
    },
    async recommendVendor(ev) {
      if (!confirm(`Recommend ${ev.vendor.name} for award?`)) return;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/vendors/${ev.vendor_id}/recommend`);
        this.toast.success('Vendor recommended for award');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to recommend vendor');
      }
    },
    async awardVendor(ev) {
      if (!confirm(`Award contract to ${ev.vendor.name}? This will update the linked PR to "Sourcing" status.`)) return;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/award`, { vendor_id: ev.vendor_id });
        this.toast.success('Contract awarded');
        await this.loadEvent();
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to award contract');
      }
    },
    async allowRequote(ev) {
      ev._allowingRequote = true;
      try {
        await axios.post(`/procurement/sourcing-events/${this.event.id}/vendors/${ev.vendor_id}/allow-requote`);
        ev.allow_requote = true;
        this.toast.success(`${ev.vendor.name} can now update their bid`);
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to enable bid update');
      } finally {
        ev._allowingRequote = false;
      }
    },
    async doAddClarification() {
      this.addingClar = true;
      try {
        const { data } = await axios.post(`/procurement/sourcing-events/${this.event.id}/clarifications`, {
          ...this.clarForm, vendor_id: this.clarForm.vendor_id || null,
        });
        this.clarifications.push({ ...data.data, _answer: '', _isPublic: false });
        this.toast.success('Clarification added');
        this.showClarModal = false;
        this.clarForm = { vendor_id: '', asked_by: '', question: '' };
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to add clarification');
      } finally { this.addingClar = false; }
    },
    async answerClarification(c) {
      if (!c._answer.trim()) { this.toast.error('Enter an answer'); return; }
      try {
        const { data } = await axios.patch(
          `/procurement/sourcing-events/${this.event.id}/clarifications/${c.id}`,
          { answer: c._answer, is_public: c._isPublic }
        );
        const idx = this.clarifications.findIndex(x => x.id === c.id);
        if (idx !== -1) this.clarifications[idx] = { ...data.data, _answer: '', _isPublic: false };
        this.toast.success('Clarification answered');
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to answer clarification');
      }
    },
    openGeneratePo(ev) {
      this.selectedPoVendor = ev;
      this.generatePoForm = {
        title: this.event.title,
        delivery_address: this.event.delivery_address || '',
        payment_terms: this.event.payment_terms || '',
        expected_delivery_date: '',
        notes: '',
      };
      this.showGeneratePoModal = true;
    },
    async doGeneratePo() {
      if (!this.generatePoForm.title.trim()) { this.toast.error('PO title is required'); return; }
      this.generatingPo = true;
      try {
        const { data } = await axios.post(`/procurement/sourcing-events/${this.event.id}/generate-po`, {
          vendor_id: this.selectedPoVendor.vendor_id,
          ...this.generatePoForm,
        });
        this.toast.success('Purchase order created: ' + data.data.po_number);
        this.showGeneratePoModal = false;
        this.$router.push(`/procurement/purchase-orders/${data.data.id}`);
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to generate PO');
      } finally {
        this.generatingPo = false;
      }
    },
    formatStatus(s) {
      const map = {
        draft:'Draft', open:'Open', closed:'Closed',
        evaluation:'Evaluation', award_recommended:'Award Recommended',
        awarded:'Awarded', cancelled:'Cancelled',
      };
      return map[s] ?? s;
    },
    getEventLine(eventLineId) {
      return this.event?.lines?.find(l => l.id === eventLineId) || null;
    },
    formatVendorStatus(s) {
      const map = { invited:'Invited', acknowledged:'Acknowledged', declined:'Declined', submitted:'Submitted', disqualified:'Disqualified' };
      return map[s] ?? s;
    },
    formatCurrency(amount, currency) {
      const sym = { NGN:'₦', GBP:'£', USD:'$', EUR:'€' }[currency] ?? (currency ?? '');
      return sym + Number(amount).toLocaleString('en', { minimumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
    },
    formatDatetime(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    },
    isOverdue(d) { return d && new Date(d) < new Date(); },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.back-btn    { display:inline-flex; align-items:center; gap:6px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:6px; }
.back-btn:hover { color:#004AAD; }
.title-row   { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0; }
.page-sub    { font-size:13px; color:#7a90b0; margin:4px 0 0; }
.me-1 { margin-right:4px; }
.ms-1 { margin-left:4px; }

.header-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

.loading-state { display:flex; flex-direction:column; align-items:center; padding:60px 24px; gap:10px; color:#7a90b0; }
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

.detail-layout { display:grid; grid-template-columns:1fr 300px; gap:20px; align-items:start; }
@media(max-width:960px) { .detail-layout { grid-template-columns:1fr; } }

/* Tabs */
.tab-bar { display:flex; gap:2px; border-bottom:2px solid #e4eaf2; margin-bottom:0; }
.tab-btn { padding:10px 18px; font-size:13px; font-weight:600; background:transparent; border:none; color:#7a90b0; cursor:pointer; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; }
.tab-btn.active { color:#004AAD; border-bottom-color:#004AAD; }
.tab-btn:hover:not(.active) { color:#1a2b45; }
.tab-pane { background:#fff; border:1px solid #e4eaf2; border-top:none; border-radius:0 0 12px 12px; padding:20px; }

.tab-pane-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.tab-pane-title  { font-size:13px; font-weight:700; color:#1a2b45; }

.inner-table { width:100%; border-collapse:collapse; font-size:13px; }
.inner-table th { padding:9px 12px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; }
.inner-table td { padding:11px 12px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:top; }
.inner-table tr:last-child td { border-bottom:none; }
.line-desc { font-weight:600; }
.line-spec { font-size:11.5px; margin-top:2px; }

.empty-state-sm { text-align:center; padding:32px 24px; color:#aab8cc; font-size:14px; }

/* Vendor list */
.vendor-list { display:flex; flex-direction:column; gap:12px; }
.vendor-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px 18px; }
.vendor-card-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; }
.vendor-name { font-weight:700; font-size:14px; color:#1a2b45; }
.vendor-num  { font-size:11.5px; margin-top:2px; }
.vendor-card-right { display:flex; flex-direction:column; align-items:flex-end; gap:4px; }
.vendor-status { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.vendor-status--invited      { background:#eff6ff; color:#1d4ed8; }
.vendor-status--acknowledged { background:#f0fdf4; color:#15803d; }
.vendor-status--declined     { background:#fee2e2; color:#dc2626; }
.vendor-status--submitted    { background:#dcfce7; color:#065f46; }
.vendor-status--disqualified { background:#f1f5f9; color:#64748b; }
.recommended-badge { background:#fef9c3; color:#854d0e; font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }
.quote-row   { display:flex; align-items:center; gap:8px; margin-bottom:6px; font-size:13px; }
.quote-label { color:#7a90b0; font-size:12px; }
.quote-amount { font-weight:700; color:#004AAD; }
.eval-notes  { font-size:12px; color:#64748b; font-style:italic; margin-bottom:8px; }
.btn-view-bid { background:#eff6ff; color:#004AAD; border:1.5px solid #bfdbfe; padding:3px 10px; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap; }
.btn-view-bid:hover { background:#dbeafe; }

/* Bid breakdown */
.bid-breakdown { background:#f0f7ff; border:1px solid #bfdbfe; border-radius:8px; padding:14px 16px; margin:8px 0; }
.bid-breakdown-meta { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:12px; }
.bid-meta-chip { background:#fff; border:1px solid #dde8f8; border-radius:6px; padding:4px 12px; display:inline-flex; flex-direction:column; }
.bid-meta-label { font-size:10px; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; }
.bid-meta-val   { font-size:13px; font-weight:600; color:#1a2b45; }
.bid-meta-total { color:#004AAD; }
.bid-table { width:100%; border-collapse:collapse; font-size:12.5px; background:#fff; border-radius:6px; overflow:hidden; }
.bid-table th { background:#e8f0fb; color:#4a6080; font-weight:600; font-size:11px; text-transform:uppercase; padding:7px 10px; text-align:left; border-bottom:1px solid #dde8f8; white-space:nowrap; }
.bid-table td { padding:7px 10px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
.bid-table tbody tr:last-child td { border-bottom:none; }
.bid-tfoot td { border-top:2px solid #dde8f8; padding:8px 10px; background:#f8fafc; }
.bid-tfoot-label { font-size:12px; font-weight:600; color:#4a6080; }
.bid-tfoot-total { font-weight:700; color:#004AAD; font-size:13px; text-align:right; }
.ta-r   { text-align:right; }
.fw-bold { font-weight:700; }
.spec-note  { display:block; font-size:11px; color:#94a3b8; font-style:italic; margin-top:2px; }
.bid-cover-note { margin-top:12px; background:#fff; border:1px solid #dde8f8; border-radius:6px; padding:10px 14px; }
.vendor-actions { border-top:1px solid #e4eaf2; padding-top:12px; margin-top:8px; }
.quote-fields { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:10px; align-items:center; }
.mini-input  { flex:1; min-width:120px; padding:6px 10px; border:1.5px solid #dde5f0; border-radius:7px; font-size:12px; outline:none; }
.vendor-action-btns { display:flex; gap:8px; flex-wrap:wrap; }

/* Clarifications */
.clarification-list { display:flex; flex-direction:column; gap:12px; }
.clar-card   { border:1px solid #e4eaf2; border-radius:10px; padding:14px 18px; background:#fff; }
.clar-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.clar-meta   { display:flex; align-items:center; gap:6px; }
.clar-asker  { font-weight:700; font-size:13px; color:#1a2b45; }
.clar-vendor { font-size:12px; }
.public-badge { background:#dcfce7; color:#15803d; font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }
.clar-question { font-size:13.5px; color:#1a2b45; margin-bottom:8px; }
.clar-answer { font-size:13px; color:#374151; background:#f0fdf4; padding:10px 14px; border-radius:8px; border-left:3px solid #16a34a; }
.clar-unanswered { margin-top:8px; }
.answer-form { display:flex; flex-direction:column; gap:8px; }
.answer-form-row { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
.checkbox-label { display:flex; align-items:center; gap:6px; font-size:12.5px; color:#374151; cursor:pointer; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; background:#fff; width:100%; box-sizing:border-box; resize:vertical; }
.field-input:focus { border-color:#004AAD; }

/* Sidebar */
.side-col { display:flex; flex-direction:column; gap:14px; }
.meta-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; }
.meta-title { font-size:13px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.06em; margin-bottom:14px; padding-bottom:8px; border-bottom:1px solid #f0f4f8; }
.meta-row   { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; font-size:13px; color:#1a2b45; padding:6px 0; border-bottom:1px solid #f8fafc; flex-wrap:wrap; }
.meta-row:last-child { border-bottom:none; }
.meta-label { color:#7a90b0; font-size:12px; white-space:nowrap; }
.instructions-text { font-size:13px; color:#374151; line-height:1.6; margin:0; white-space:pre-wrap; }
.award-winner { font-weight:700; color:#15803d; }
.date-overdue { color:#dc2626; font-weight:600; }

/* Badges */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft             { background:#f1f5f9; color:#64748b; }
.status-badge--open              { background:#dcfce7; color:#15803d; }
.status-badge--closed            { background:#fef9c3; color:#854d0e; }
.status-badge--evaluation        { background:#e0f2fe; color:#0369a1; }
.status-badge--award_recommended { background:#fff7ed; color:#c2410c; }
.status-badge--awarded           { background:#d1fae5; color:#065f46; }
.status-badge--cancelled         { background:#fee2e2; color:#dc2626; }

.type-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:4px; padding:3px 8px; letter-spacing:.05em; }
.type-badge--rfq { background:#eff6ff; color:#1d4ed8; }
.type-badge--rfp { background:#f5f3ff; color:#6d28d9; }
.type-badge--rfi { background:#fff7ed; color:#c2410c; }
.type-badge--eoi { background:#f0fdf4; color:#15803d; }

.mono   { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.text-muted { color:#aab8cc; font-size:12px; }

/* Buttons */
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-warning { display:inline-flex; align-items:center; gap:6px; background:#f59e0b; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-warning:hover:not(:disabled) { background:#d97706; }
.btn-warning:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.5; cursor:not-allowed; }
.btn-sm { padding:5px 14px; border-radius:7px; border:1.5px solid #dde5f0; background:#fff; font-size:12px; font-weight:600; cursor:pointer; color:#1a2b45; }
.btn-sm:hover { background:#f4f7fb; }
.btn-sm--blue  { background:#eff6ff; color:#004AAD; border-color:#bfdbfe; }
.btn-sm--blue:hover { background:#dbeafe; }
.btn-sm--green { background:#dcfce7; color:#15803d; border-color:#86efac; }
.btn-sm--green:hover { background:#bbf7d0; }
.btn-sm--danger { background:#fee2e2; color:#dc2626; border-color:#fecaca; }
.btn-sm--danger:hover { background:#fecaca; }
.btn-sm--amber { background:#fffbeb; color:#92400e; border-color:#fde68a; }
.btn-sm--amber:hover { background:#fef3c7; }
.vendor-requote-row { display:flex; align-items:center; gap:8px; margin:8px 0 4px; }
.requote-enabled-badge { background:#dcfce7; color:#15803d; font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; border:1px solid #86efac; }
.btn-sm-add { display:inline-flex; align-items:center; gap:5px; background:#eff6ff; color:#004AAD; border:1.5px solid #bfdbfe; border-radius:7px; padding:5px 12px; font-size:12px; font-weight:600; cursor:pointer; }
.btn-sm-add:hover { background:#dbeafe; }

/* Modals */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:500px; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
.modal-header h6 { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.btn-close-modal { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; }
.modal-body { padding:20px 24px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; }
.field { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:12.5px; font-weight:600; color:#374151; }
.req { color:#dc2626; }

/* Close event confirm modal */
.modal-box--sm { max-width:420px; }

.modal-header--warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-bottom: 1px solid #fde68a;
  padding: 24px 24px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.modal-header-icon {
  width: 48px; height: 48px;
  background: #f59e0b;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 20px;
  box-shadow: 0 4px 12px rgba(245,158,11,.3);
}

.modal-body--center { text-align: center; padding: 24px 28px 20px; }

.modal-confirm-title {
  font-size: 18px; font-weight: 700; color: #1a2b45;
  margin: 0 0 8px;
}
.modal-confirm-desc {
  font-size: 13.5px; color: #4b5563; line-height: 1.6;
  margin: 0 0 18px;
}

.modal-confirm-meta {
  background: #f8fafc;
  border: 1px solid #e4eaf2;
  border-radius: 10px;
  padding: 2px 0;
  text-align: left;
}
.modal-confirm-meta-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 9px 16px;
  font-size: 13px;
  border-bottom: 1px solid #f0f4f8;
}
.modal-confirm-meta-row:last-child { border-bottom: none; }
.modal-confirm-meta-label { color: #7a90b0; font-size: 12px; font-weight: 500; }
.modal-confirm-meta-val   { font-weight: 600; color: #1a2b45; }

/* Bid locked */
.bid-locked-notice { display:inline-flex; align-items:center; gap:6px; background:#fef9c3; color:#854d0e; font-size:12px; font-weight:600; padding:5px 12px; border-radius:6px; border:1px solid #fde68a; margin-bottom:6px; }

/* Analysis button */
.btn-analysis { color:#6d28d9; border-color:#ddd6fe; }
.btn-analysis:hover:not(:disabled) { background:#f5f3ff; }

/* Generate PO vendor info */
.po-gen-vendor-info { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 16px; margin-bottom:16px; }
.po-gen-vendor-name { font-weight:700; font-size:15px; color:#1a2b45; }
.po-gen-amount { font-size:13px; color:#374151; margin-top:4px; }

/* Generate PO teal button */
.btn-sm--teal { background:#ecfdf5; color:#065f46; border-color:#6ee7b7; }
.btn-sm--teal:hover { background:#d1fae5; }
</style>
